import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { useEffect, useState } from "react";
import { useSession } from "./context/SessionContext";
import { useNavigate } from "react-router-dom";
import SettingsModal from "./components/SettingsModal";
import { CLUB_ID, THEME_COLOR } from "./config/club";
import Swal from "sweetalert2";
import SessionSummaryModal from "./components/SessionSummaryModal";

export default function SelectSession() {
  const [sessions, setSessions] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const { setSessionId, setSession } = useSession();
  const [selectedSession, setSelectedSession] = useState(null);
  const [openMonths, setOpenMonths] = useState({});
  const toggleMonth = (month) => {
    setOpenMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };
  const navigate = useNavigate();
  const clubId = CLUB_ID;
  const Toast = Swal.mixin({
    toast: true,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
  const ToastShow = (type, message) => {
    Toast.fire({
      icon: type,
      title: message,
    });
  };
  // Helper to create transparent versions of your theme color
  const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;

  const loadSessions = async () => {
    const q = query(
      collection(db, "clubs", clubId, "sessions"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    loadSessions();
  }, [clubId]);

  const activeSessions = sessions.filter(s => s.status === "active");
  const endedSessions = sessions.filter(s => s.status === "ended");
  const groupSessionsByMonth = (sessions) => {
    const groups = {};

    sessions.forEach((s) => {
      if (!s.createdAt?.seconds) return;

      const date = new Date(s.createdAt.seconds * 1000);
      const monthKey = date.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });

      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(s);
    });

    return groups;
  };

  const groupedEndedSessions = groupSessionsByMonth(endedSessions);
  return (
    <div className="container py-5" style={{ maxWidth: "900px" }}>
      <div className="mb-4">
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="btn d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill border-0 shadow-sm transition-all"
          style={{ backgroundColor: `${THEME_COLOR}25`, color: THEME_COLOR }}
        >
          <i className="bi bi-arrow-left-short fs-4"></i>
          <span className="fw-bold small">Back to Dashboard</span>
        </button>
      </div>
      {/* HEADER SECTION */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-5 gap-3">
        <div>
          <h1 className="fw-bold mb-1" style={{ color: THEME_COLOR, letterSpacing: '-1.5px', fontSize: '2.5rem' }}>
            Sessions
          </h1>
          <p className="text-muted m-0 opacity-75">Manage and monitor your club activities</p>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          {/* Navigation Actions */}
          {/* {[
    { label: "Players", path: "/admin/players", icon: "bi-people-fill" },
    { label: "Jersey Orders", path: "/admin/jersey", icon: "bi-tag-fill" },
    { label: "Tournament", path: "/admin/registrations", icon: "bi-trophy-fill" },
  ].map((btn) => (
    <button
      key={btn.label}
      className="btn px-4 py-2 rounded-pill fw-bold border-0 transition-all"
      style={{
        backgroundColor: `${THEME_COLOR}15`,
        color: THEME_COLOR,
        fontSize: '0.9rem'
      }}
      onClick={() => navigate(btn.path)}
    >
      <i className={`${btn.icon} me-2`}></i>
      {btn.label}
    </button>
  ))} */}

          {/* Primary Action - Pushed to the end on larger screens */}
          <button
            className="btn shadow-sm px-4 py-2 rounded-pill fw-bold border-0 text-white ms-md-auto"
            style={{ backgroundColor: THEME_COLOR }}
            onClick={() => setShowSettings(true)}
          >
            <i className="bi bi-plus-lg me-1"></i> New Session
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* 🟢 ACTIVE SESSIONS */}
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981', marginRight: 10 }}></div>
            <h6 className="text-uppercase small fw-bold m-0 text-muted tracking-wider">Live Now</h6>
          </div>

          {activeSessions.length === 0 && (
            <div className="rounded-4 p-5 text-center border-0" style={{ backgroundColor: themeAlpha('05'), border: `1px dashed ${themeAlpha('20')}` }}>
              <p className="text-muted m-0">No sessions are currently active.</p>
            </div>
          )}

          <div className="row g-3">
            {activeSessions.map(s => (
              <div key={s.id} className="col-md-6 col-lg-4">
                <div
                  className="card h-100 border-0 shadow-sm p-4 hover-lift"
                  style={{
                    borderRadius: '24px',
                    background: `linear-gradient(135deg, var(--card-bg) 0%, ${themeAlpha('08')} 100%)`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${themeAlpha('10')}`
                  }}
                  onClick={() => {
                    setSessionId(s.id);
                    setSession(s);
                    navigate("/admin");
                  }}
                >
                  <div className="mb-4">
                    <span className="badge rounded-pill mb-2" style={{ backgroundColor: themeAlpha('15'), color: THEME_COLOR }}>
                      Active
                    </span>
                    <h4 className="fw-bold d-block" style={{ color: 'var(--text-color)' }}>{s.name}</h4>
                  </div>

                  <div className="mt-auto d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {new Date(s.createdAt?.seconds * 1000).toLocaleString("en-US", { dateStyle: "medium", })}
                    </small>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 35, height: 35, backgroundColor: THEME_COLOR, color: 'white' }}
                    >
                      →
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 🔴 ENDED SESSIONS (Minimalist Laptop List) */}
        {/* 🔴 SESSION HISTORY */}
        {/* 🔴 SESSION HISTORY */}
        {endedSessions.length > 0 && (
          <div className="col-12 mt-5">

            <h6 className="text-uppercase small fw-bold text-muted mb-3">
              Session History
            </h6>

            {Object.entries(groupedEndedSessions).map(([month, monthSessions]) => {

              const isOpen = openMonths[month] ?? true;

              return (
                <div key={month} className="mb-3">

                  {/* Month Header */}
                  <div
                    onClick={() => toggleMonth(month)}
                    className="d-flex justify-content-between align-items-center px-4 py-3 rounded-4 month-header"
                    style={{
                      cursor: "pointer",
                      background: "var(--card-bg)",
                      border: "1px solid var(--border-color)",
                      transition: "all 0.2s ease"
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">

                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 28,
                          height: 28,
                          background: `${THEME_COLOR}`,
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 700
                        }}
                      >
                        {isOpen ? "−" : "+"}
                      </div>

                      <div>
                        <span className="fw-bold" style={{ color: "var(--text-color)" }}>
                          {month}
                        </span>

                        <div className="small text-muted">
                          {monthSessions.length} session{monthSessions.length > 1 && "s"}
                        </div>
                      </div>

                    </div>

                    <i
                      className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
                      style={{ color: "var(--text-muted)" }}
                    />
                  </div>

                  {/* Session List */}
                  {isOpen && (
                    <div
                      className="mt-2 rounded-4 overflow-hidden"
                      style={{
                        background: "var(--card-bg)",
                        border: "1px solid var(--border-color)"
                      }}
                    >

                      {monthSessions.map((s, index) => (
                        <div
                          key={s.id}
                          className={`d-flex justify-content-between align-items-center py-3 px-4 ${index !== monthSessions.length - 1 ? "border-bottom" : ""
                            }`}
                          style={{ transition: "background 0.2s" }}
                        >

                          <div>
                            <span
                              className="fw-bold d-block"
                              style={{ color: "var(--text-color)" }}
                            >
                              {s.name}
                            </span>

                            <small className="text-muted">
                              {new Date(
                                s.createdAt?.seconds * 1000
                              ).toLocaleDateString("en-US", {
                                day: "numeric",
                                month: "short",
                                year: "numeric"
                              })}
                            </small>
                          </div>

                          <div className="d-flex gap-2">

                            <button
                              className="btn btn-sm px-3 rounded-pill fw-bold border"
                              onClick={() => setSelectedSession(s)}
                              style={{
                                color: "var(--text-muted)",
                                borderColor: "var(--border-color)"
                              }}
                            >
                              Summary
                            </button>

                            <button
                              className="btn btn-sm px-3 rounded-pill fw-bold border"
                              style={{
                                color: THEME_COLOR,
                                borderColor: `${THEME_COLOR}55`,
                                background: `${THEME_COLOR}10`
                              }}
                              onClick={async () => {
                                try {
                                  const matchesRef = collection(
                                    db,
                                    "clubs",
                                    clubId,
                                    "sessions",
                                    s.id,
                                    "matches"
                                  );

                                  const q = query(matchesRef, orderBy("createdAt", "asc"));
                                  const snap = await getDocs(q);

                                  if (snap.empty) {
                                    ToastShow("info", "No match history found.");
                                    return;
                                  }

                                  const matches = snap.docs.map((doc, index) => {
                                    const data = doc.data();

                                    const players = data.players || [];

                                    const team1 = players
                                      .slice(0, 2)
                                      .map((p) => p.name)
                                      .join(" & ");

                                    const team2 = players
                                      .slice(2, 4)
                                      .map((p) => p.name)
                                      .join(" & ");

                                    const startedAt = data.startedAt?.toDate
                                      ? data.startedAt.toDate()
                                      : null;

                                    const endedAt = data.endedAt?.toDate
                                      ? data.endedAt.toDate()
                                      : null;

                                    let duration = "-";

                                    if (startedAt && endedAt) {
                                      const mins = Math.round(
                                        (endedAt.getTime() - startedAt.getTime()) / 60000
                                      );

                                      duration = `${mins} mins`;
                                    }

                                    return {
                                      Match: index + 1,
                                      Court: data.court || "-",
                                      Team1: team1 || "-",
                                      Team2: team2 || "-",
                                      Score: `="${data.score || "-"}"`,
                                      Status: data.status || "-",
                                      Source: data.source || "-",
                                      Started: startedAt
                                        ? startedAt.toLocaleString()
                                        : "-",
                                      Ended: endedAt
                                        ? endedAt.toLocaleString()
                                        : "-",
                                      Duration: duration
                                    };
                                  });

                                  const headers = Object.keys(matches[0]).join(",");

                                  const rows = matches.map((row) =>
                                    Object.values(row)
                                      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                                      .join(",")
                                  );

                                  const csvContent = [headers, ...rows].join("\n");

                                  const blob = new Blob([csvContent], {
                                    type: "text/csv;charset=utf-8;"
                                  });

                                  const url = URL.createObjectURL(blob);

                                  const link = document.createElement("a");

                                  link.href = url;

                                  link.setAttribute(
                                    "download",
                                    `${s.name.replace(/\s+/g, "_")}_match_history.csv`
                                  );

                                  document.body.appendChild(link);

                                  link.click();

                                  document.body.removeChild(link);

                                  URL.revokeObjectURL(url);

                                  ToastShow("success", "Match history downloaded.");
                                } catch (err) {
                                  console.error(err);
                                  ToastShow("error", "Failed to download match history.");
                                }
                              }}
                            >
                              <i className="bi bi-download me-1"></i>
                              Match History
                            </button>
                            <button
                              className="btn btn-sm px-3 rounded-pill fw-bold border"
                              style={{
                                color: THEME_COLOR,
                                borderColor: `${THEME_COLOR}55`,
                                background: `${THEME_COLOR}10`
                              }}
                              onClick={async () => {
                                try {
                                  const matchesRef = collection(
                                    db,
                                    "clubs",
                                    clubId,
                                    "sessions",
                                    s.id,
                                    "matches"
                                  );

                                  const q = query(matchesRef, orderBy("createdAt", "asc"));
                                  const snap = await getDocs(q);

                                  if (snap.empty) {
                                    ToastShow("info", "No players found.");
                                    return;
                                  }

                                  const playersMap = new Map();

                                  snap.docs.forEach((doc) => {
                                    const data = doc.data();
                                    const players = data.players || [];

                                    players.forEach((player) => {
                                      if (player?.name) {
                                        // Use player ID if available to prevent duplicate names
                                        const key = player.id || player.uid || player.name;

                                        if (!playersMap.has(key)) {
                                          playersMap.set(key, player.name);
                                        }
                                      }
                                    });
                                  });

                                  const playerNames = Array.from(playersMap.values())
                                    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                                    .map((name, index) => `${index + 1}. ${name}`);

                                  if (playerNames.length === 0) {
                                    ToastShow("info", "No players found.");
                                    return;
                                  }

                                  // One player per line
                                  const text = playerNames.join("\n");

                                  await navigator.clipboard.writeText(text);

                                  ToastShow(
                                    "success",
                                    `${playerNames.length} players copied to clipboard.`
                                  );
                                } catch (err) {
                                  console.error(err);
                                  ToastShow("error", "Failed to copy players.");
                                }
                              }}
                            >
                              <i className="bi bi-clipboard me-1"></i>
                              Copy Players
                            </button>
                          </div>

                        </div>
                      ))}

                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}
      </div>

      {showSettings && (
        <SettingsModal
          show
          ToastShow={ToastShow}
          clubId={clubId}
          defaultTab="sessions"
          onClose={() => {
            setShowSettings(false);
            loadSessions();
          }}
        />
      )}
      {selectedSession && (
        <SessionSummaryModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}
      {/* Adding a small global style for the hover effect */}
      <style>{`
        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px ${themeAlpha('15')} !important;
          border-color: ${themeAlpha('30')} !important;
        }
      `}</style>
    </div>
  );
}